/**
 * SMS sender name ("Sender ID") for FireText texts sent on behalf of a shop.
 *
 * SHARED FILE: the canonical copy lives in packages/utils/src/sms-sender-name.ts (used by
 * apps/web via @book4/utils). apps/api/src/lib/sms-sender-name.ts is a byte-for-byte copy so
 * production `node dist/` never loads a workspace package's .ts source. A parity test in the
 * API fails if the two drift, so edit both together. No imports allowed here.
 *
 * Shops cannot choose a sender name. It is always derived from the CURRENT business name with
 * deriveSmsSenderName(). When nothing usable can be derived the caller passes no sender at all,
 * so sendSms() uses the platform default (FIRETEXT_FROM env, "Book4" in production).
 * Any value stored in Business.smsSenderName (editable briefly in #50) is ignored.
 *
 * FireText `from` parameter (https://www.firetext.co.uk/docs, "Send A Message"):
 *   "This field can only be alpha numeric. [A-Z], [a-z], [0-9], Min 3 chars, max 11 chars."
 * Book4 rule (stricter): 3 to 11 characters, A-Z a-z 0-9 only (no spaces or symbols), and it
 * must start with a letter (an all-digit or digit-first ID can be treated as a phone number by
 * networks). A few reserved words that networks are likely to filter are also refused.
 */

export const SMS_SENDER_NAME_MIN_LENGTH = 3;
export const SMS_SENDER_NAME_MAX_LENGTH = 11;
/**
 * Display-only label for the platform sender (what guests see when a shop has no usable derived
 * name). Never pass this to sendSms(): leave `from` out so FIRETEXT_FROM is used.
 */
export const DEFAULT_SMS_SENDER_NAME = "Book4";

const SENDER_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9]{2,10}$/;

/**
 * Reserved words (case-insensitive). Networks and the UK SMS SenderID Protection Registry
 * filter IDs that look like banks, government, delivery firms or big brands, and generic
 * words that are common in scams. Kept deliberately small to avoid blocking real shop names.
 */
const RESERVED_EXACT = new Set([
  "info", "verify", "verified", "alert", "alerts", "security", "secure", "fraud", "support",
  "help", "admin", "noreply", "sms", "text", "otp", "code", "notice", "update", "account",
  "bank", "police", "nhs", "gov", "dwp", "council", "tvlicence", "tvlicensing",
  "postoffice", "evri", "hermes", "dpd", "dhl", "ups", "fedex", "yodel",
  "amazon", "apple", "google", "microsoft", "netflix", "ebay", "whatsapp", "facebook", "instagram",
  "lloyds", "halifax", "nationwide", "monzo", "starling", "revolut", "tsb", "rbs", "vodafone",
]);
/** Distinctive tokens blocked anywhere in the name (low risk of matching a real shop name). */
const RESERVED_CONTAINS = [
  "hmrc", "dvla", "govuk", "royalmail", "parcelforce", "paypal", "barclays", "natwest", "santander", "hsbc",
];

export function isReservedSmsSenderName(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (RESERVED_EXACT.has(v)) return true;
  return RESERVED_CONTAINS.some((token) => v.includes(token));
}

/** True when the value is a sender FireText accepts and Book4 allows (format + blocklist). */
export function isValidSmsSenderName(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return SENDER_NAME_PATTERN.test(v) && !isReservedSmsSenderName(v);
}

/** Latin letters that NFKD does not split into base letter + accent. */
const LETTER_FOLDS: Record<string, string> = {
  "ß": "ss", "æ": "ae", "Æ": "AE", "ø": "o", "Ø": "O", "œ": "oe", "Œ": "OE",
  "ł": "l", "Ł": "L", "đ": "d", "Đ": "D", "ð": "d", "Ð": "D", "þ": "th", "Þ": "Th", "ı": "i",
};

/**
 * Sender name for a shop's texts, derived from the CURRENT business name. Returns null when no
 * usable name can be derived; the caller then leaves `from` out so FIRETEXT_FROM is used.
 *
 *  1. Accents are folded (Café -> Cafe), apostrophes dropped (Jo's -> Jos), and every other
 *     space, symbol or emoji splits words (FireText allows A-Z a-z 0-9 only).
 *  2. Leading non-letters are dropped so it starts with a letter (123 Barbers -> Barbers).
 *     A leading "The" (any case, whole word) is then skipped (The Cut Club -> CutClub), unless
 *     what follows would not start with a letter or would leave fewer than 3 characters, in which
 *     case "The" is kept (The Ox -> TheOx, The -> The). "The" is also kept when the name without
 *     it would hit the blocklist (The Bank -> TheBank). No other filler words are skipped.
 *  3. If the joined words fit in 11 characters they are used as they are (RN Clipz -> RNClipz).
 *     Otherwise whole trailing words are dropped while at least 3 characters remain
 *     (Conor Fox Hair -> ConorFox, Johns Barber Shop -> JohnsBarber).
 *     Only when that is impossible (the first word alone is longer than 11, or the words that
 *     fit come to fewer than 3 characters) is the joined name hard-cut at 11
 *     (CustomerSuccess -> CustomerSuc).
 *  4. The result must pass the blocklist and the FireText format rule, else null
 *     (fewer than 3 characters, "HMRC", only emoji, and so on).
 */
export function deriveSmsSenderName(businessName: string | null | undefined): string | null {
  if (!businessName) return null;
  const folded = businessName
    .replace(/[ßæÆøØœŒłŁđĐðÐþÞı]/g, (ch) => LETTER_FOLDS[ch] ?? ch)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2018\u2019\u02bc`\u00b4]/g, "");
  const words = folded.split(/[^A-Za-z0-9]+/).filter(Boolean);

  // Must start with a letter: drop leading digits (and digit-only words).
  while (words.length > 0 && !/^[A-Za-z]/.test(words[0])) {
    const rest = words[0].replace(/^[^A-Za-z]+/, "");
    if (rest) words[0] = rest;
    else words.shift();
  }
  if (words.length === 0) return null;

  // Skip a leading "The" when the rest still makes a name (at least 3 characters, starts with a
  // letter) and is not blocklisted. The blocklist is checked first, so "The Bank" keeps "The".
  if (words.length > 1 && words[0].toLowerCase() === "the" && /^[A-Za-z]/.test(words[1])) {
    const rest = words.slice(1);
    if (rest.join("").length >= SMS_SENDER_NAME_MIN_LENGTH) {
      const withoutThe = fitSenderWords(rest);
      if (!isReservedSmsSenderName(withoutThe)) {
        return isValidSmsSenderName(withoutThe) ? withoutThe : null;
      }
    }
  }

  const candidate = fitSenderWords(words);
  return isValidSmsSenderName(candidate) ? candidate : null;
}

/** Step 3: join words, dropping whole trailing words (or hard-cutting) to fit 11 characters. */
function fitSenderWords(words: string[]): string {
  const joined = words.join("");
  if (joined.length <= SMS_SENDER_NAME_MAX_LENGTH) return joined;
  let fit = "";
  for (const w of words) {
    if ((fit + w).length > SMS_SENDER_NAME_MAX_LENGTH) break;
    fit += w;
  }
  return fit.length >= SMS_SENDER_NAME_MIN_LENGTH ? fit : joined.slice(0, SMS_SENDER_NAME_MAX_LENGTH);
}
